import { Box, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import EditNoteIcon from '@mui/icons-material/EditNote'
import QuizIcon from '@mui/icons-material/Quiz'
import SortIcon from '@mui/icons-material/Sort'
import ExtensionIcon from '@mui/icons-material/Extension'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import type { ReactElement } from 'react'
import { GameModeCard } from './GameModeCard'
import { useKidMode } from '../../contexts/KidModeContext'
import type { GameSession, GameType } from '../../types'

interface GameMode {
  type: GameType
  titleKey: string
  descriptionKey: string
  icon: ReactElement
  gradient: [string, string]
}

const KID_ALLOWED: ReadonlySet<GameType> = new Set<GameType>([
  'multiple-choice',
  'word-puzzle',
  'sequence',
])

const GAME_MODES: GameMode[] = [
  { type: 'fill-blank', titleKey: 'games.fillBlankTitle', descriptionKey: 'games.fillBlankDescription', icon: <EditNoteIcon fontSize="inherit" />, gradient: ['#0EA5E9', '#0369A1'] },
  { type: 'multiple-choice', titleKey: 'games.multipleChoiceTitle', descriptionKey: 'games.multipleChoiceDescription', icon: <QuizIcon fontSize="inherit" />, gradient: ['#f093fb', '#f5576c'] },
  { type: 'sequence', titleKey: 'games.sequenceTitle', descriptionKey: 'games.sequenceDescription', icon: <SortIcon fontSize="inherit" />, gradient: ['#4facfe', '#00f2fe'] },
  { type: 'word-puzzle', titleKey: 'games.wordPuzzleTitle', descriptionKey: 'games.wordPuzzleDescription', icon: <ExtensionIcon fontSize="inherit" />, gradient: ['#43e97b', '#38f9d7'] },
  { type: 'listen-type', titleKey: 'games.listenTypeTitle', descriptionKey: 'games.listenTypeDescription', icon: <HeadphonesIcon fontSize="inherit" />, gradient: ['#fa709a', '#fee140'] },
]

interface GamesLauncherProps {
  /** Recent sessions; used to derive the "this week" badge per game type. */
  sessions: GameSession[]
  /** Kid hub: larger single-column cards, no stat badges. */
  kidLayout?: boolean
}

/**
 * The 5 game-mode cards in a responsive grid. MUI's 12-col Grid can't
 * divide cleanly into 5, so we drop to a CSS `grid-template-columns` at
 * lg+ for a perfect 5-col row with no empty hole. Smaller breakpoints
 * fall back to 1- and 2- and 3-col layouts.
 */
export function GamesLauncher({ sessions, kidLayout = false }: GamesLauncherProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isKidMode } = useKidMode()

  const modes = isKidMode ? GAME_MODES.filter((m) => KID_ALLOWED.has(m.type)) : GAME_MODES

  // Per-game-type session count for the past 7 days (client-side derivation —
  // backend doesn't aggregate per-mode). Memoised to avoid re-walking the
  // sessions array on every parent render.
  const countsByType = useMemo(() => {
    const startOfWeek = Date.now() - 7 * 24 * 60 * 60 * 1000
    const c = new Map<GameType, number>()
    for (const s of sessions) {
      if (new Date(s.playedAt).getTime() < startOfWeek) continue
      c.set(s.gameType, (c.get(s.gameType) ?? 0) + 1)
    }
    return c
  }, [sessions])

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: kidLayout
          ? { xs: '1fr', sm: '1fr', md: 'repeat(3, 1fr)' }
          : {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
        mb: 3,
      }}
    >
      {modes.map((mode) => {
        const count = countsByType.get(mode.type) ?? 0
        return (
          <Box key={mode.type} sx={{ position: 'relative', display: 'flex' }}>
            <GameModeCard
              title={t(mode.titleKey)}
              description={t(mode.descriptionKey)}
              icon={mode.icon}
              gradient={mode.gradient}
              onClick={() => navigate(`/games/${mode.type}`)}
              size={kidLayout ? 'kid' : 'default'}
            />
            {count > 0 && !kidLayout && (
              <Chip
                label={t('games.gamesPlayedBadge', { count })}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}

